import React, { useContext } from 'react';
import { useQuery } from '@apollo/client';
import { parseUrl } from 'query-string';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { MainContext } from '@blaze-cms/nextjs-components';
import { generateSingleItemQuery, getItemList } from '../../application/query';
import ListBuilder from './ListBuilder';
import { AZ_LIST_TYPE } from './constants';
import {
  getAzQueryFilter,
  getSortProps,
  hasRequiredItemListDetails,
  getListSpecificSearchFilter,
  getListQuery
} from './helpers';
import {
  getGenericProps,
  getRequiredSchemas,
  checkForError,
  getUnpublishedEntityName,
  getPaginationIndex,
  getSearchFilterType,
  getElasticsearchOperator,
  buildSearchValuesText,
  getQueryFilters,
  getInheritedFilters,
  getQueryProps
} from '../../helpers';
import { getItemListId, getItemListIds } from '../ItemList/helpers';
import { useGetEntitySchemasAsObj } from '../../hooks';
import { AND_OPERATOR, TEXT_SEARCH, ID, AND, FULL } from '../../constants';

export const VariantContext = React.createContext(null);

const ListFactory = props => {
  const {
    entity: legacyEntity,
    entities,
    parent,
    name,
    filterByProperty,
    filterBy,
    omitWrappers,
    type,
    sortby: defaultSortBy,
    sort: defaultSort,
    allSearchFilters,
    operator: filterOperator,
    sortProperties,
    itemListName,
    VariantComponent
  } = props;
  const { isPreview } = useContext(MainContext);
  const router = useRouter();
  const { asPath } = router;
  const parsedQuery = asPath.replace(/%5D/g, ']').replace(/%5B/g, '[');
  const [searchFilter, hasListSpecificFilters] = getListSpecificSearchFilter(
    allSearchFilters,
    name
  );
  const { query, url, query: { itemListId: queryItemListId } = {} } = parseUrl(parsedQuery);
  const updatedQuery = getListQuery(query, hasListSpecificFilters, name);
  const isTextSearchFilterApplied = !!(
    updatedQuery.search_term &&
    searchFilter &&
    getSearchFilterType(searchFilter, TEXT_SEARCH).length
  );

  const { sort: querySort, sortby: querySortBy } = updatedQuery;

  const { sortbyFilters, updatedSortProperties } = getSortProps({
    querySort,
    sortProperties,
    defaultSort,
    querySortBy,
    defaultSortBy,
    isTextSearchFilterApplied
  });

  const itemListId = getItemListId(itemListName, queryItemListId);
  const { itemEntity: parentEntity, itemId: parentId } = parent;
  const updatedParentEntity = getUnpublishedEntityName(parentEntity);
  const paginationIndex = getPaginationIndex(updatedQuery, name);
  const azFilter = getAzQueryFilter(updatedQuery, name);

  const entitiesToUse = entities && entities.length ? entities : [legacyEntity];
  const [filterEntity] = entitiesToUse;
  const {
    data: mainSchemas = {},
    loading: schemasLoading,
    error: schemasError
  } = useGetEntitySchemasAsObj([updatedParentEntity, ...entitiesToUse]);

  const { [updatedParentEntity]: currentSchema = {}, [filterEntity]: filterEntitySchema = {} } =
    mainSchemas;

  const {
    data: requiredSchema = {},
    loading: requiredSchemaLoading,
    error: requiredSchemaError
  } = useGetEntitySchemasAsObj(
    getRequiredSchemas([...filterByProperty, ...sortbyFilters], filterEntitySchema),
    schemasLoading
  );
  const inheritedFilters = getInheritedFilters(filterBy, filterByProperty);
  const queryProps = getQueryProps(inheritedFilters, currentSchema, filterEntitySchema);
  const { actions = {} } = currentSchema || {};
  const getAction = isPreview ? actions.get : actions.getPublished;

  const {
    data: { entityData = {} } = {},
    error: itemQueryError,
    loading: itemQueryLoading
  } = useQuery(generateSingleItemQuery(getAction, queryProps), {
    variables: { id: parentId },
    skip: schemasLoading || (!inheritedFilters.length && queryProps === ID)
  });

  const {
    data: itemListData = {},
    error: itemListError,
    loading: itemListLoading
  } = useQuery(getItemList, {
    variables: { id: itemListId },
    skip: !itemListId
  });

  if (!hasRequiredItemListDetails({ itemListName, router, itemListId, queryItemListId })) return '';

  const errorsToCheck = [schemasError, requiredSchemaError, itemQueryError, itemListError];
  const { hasErr, errMsg } = checkForError(errorsToCheck);

  if (requiredSchemaLoading || schemasLoading || itemQueryLoading || itemListLoading) return '';
  if (hasErr) return errMsg;

  const itemListIds = getItemListIds(itemListData);
  const isCard = !type.includes(FULL);
  const isAZ = type.includes(AZ_LIST_TYPE);
  const genericProps = getGenericProps(props);
  const [textFilter] = getSearchFilterType(searchFilter, TEXT_SEARCH);
  const shouldApplyTextFilter = textFilter && textFilter.propsToDisplay.length;
  const operator = shouldApplyTextFilter ? getElasticsearchOperator(textFilter) : AND_OPERATOR;
  const valuesText = shouldApplyTextFilter
    ? buildSearchValuesText(textFilter, updatedQuery, name)
    : [];
  const { valuesAnd, valuesOr } = getQueryFilters(searchFilter, updatedQuery, name);

  const searchValues = {
    searchValuesText: valuesText,
    searchValuesCheckboxSelectRange: valuesAnd,
    searchValuesCheckboxOr: valuesOr
  };
  const listProps = {
    ...props,
    ...genericProps,
    query: updatedQuery,
    url,
    isCard,
    isAZ,
    router,
    sort: null,
    sortby: null,
    sortProperties: updatedSortProperties,
    azFilter,
    searchValues,
    filterEntitySchema,
    requiredSchema,
    currentSchema,
    omitWrappers,
    operator,
    paginationIndex,
    entityData,
    inheritedFilters,
    filterOperator,
    itemListIds,
    entities: entitiesToUse,
    mainSchemas
  };

  return (
    <VariantContext.Provider value={VariantComponent}>
      <ListBuilder {...listProps} />
    </VariantContext.Provider>
  );
};

ListFactory.propTypes = {
  parent: PropTypes.object.isRequired,
  type: PropTypes.string.isRequired,
  entity: PropTypes.string,
  entities: PropTypes.array,
  filterBy: PropTypes.array,
  filterByProperty: PropTypes.array,
  omitWrappers: PropTypes.bool,
  operator: PropTypes.string,
  name: PropTypes.string,
  sortby: PropTypes.array,
  sort: PropTypes.string,
  allSearchFilters: PropTypes.array,
  sortProperties: PropTypes.array,
  itemListName: PropTypes.string,
  VariantComponent: PropTypes.func
};

ListFactory.defaultProps = {
  entity: '',
  entities: [],
  filterBy: [],
  filterByProperty: [],
  omitWrappers: false,
  name: '',
  sortby: [],
  sortProperties: [],
  sort: '',
  operator: AND,
  allSearchFilters: [],
  itemListName: '',
  VariantComponent: null
};

export default ListFactory;
